import { type OperationVariables } from '@apollo/react-common';
import { type DataProxy } from 'apollo-cache';
import { type GraphQLTaggedNode } from 'react-relay';
import {
	type ConcreteRequest,
	type ReaderLinkedField,
	type ReaderScalarField,
	type ReaderSelection,
	ROOT_ID,
} from 'relay-runtime';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { type QueryCacheBridgeSource } from './cacheBridge/queryCacheBridgeSync.ts';
import { getRelayFieldStorageKey } from './relayStorageKeys.ts';
import { getRelayOperationName } from './relayOperation.ts';

export type ReadQueryInteropArgs<TVariables extends Record<string, any> = OperationVariables> =
	Pick<DataProxy.Query<TVariables>, 'variables'> & {
		query: GraphQLTaggedNode;
		/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
		__callerPackage__?: string;
	};

export function readQueryInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: ReadQueryInteropArgs<TVariables>): TData | null {
	const operationName = getRelayOperationName(params.query);
	const source: QueryCacheBridgeSource = isRelayEnabled(params.__callerPackage__, {
		operationName,
		phase: 'cache',
	})
		? 'relay'
		: 'apollo';

	// Cache reads stay native to the selected route. The cache bridge only
	// mirrors successful writes/results into the other cache.
	return readQueryInteropOriginal<TData, TVariables>(params, source);
}

function readQueryInteropOriginal<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: ReadQueryInteropArgs<TVariables>, source: QueryCacheBridgeSource): TData | null {
	return source === 'relay'
		? readQueryInteropRelay<TData, TVariables>(params)
		: readQueryInteropApollo<TData, TVariables>(params);
}

function readQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: ReadQueryInteropArgs<TVariables>,
): TData | null {
	// Guard window for SSR; this only runs client-side.
	const relayEnv = typeof window !== 'undefined' ? window.GLOBAL_RELAY_ENV : undefined;
	if (!relayEnv) {
		return null;
	}

	const source = relayEnv.getStore().getSource();
	const rootRecord = source.get(ROOT_ID);
	if (!rootRecord) {
		return null;
	}

	const queryNode = params.query as ConcreteRequest;
	const queryFields = queryNode?.operation?.selections;
	if (!queryFields) {
		return null;
	}

	const variables: Record<string, any> = (params.variables as Record<string, any>) ?? {};

	const result = readFields(source, ROOT_ID, queryFields as readonly ReaderSelection[], variables);

	return result && !result.missing ? (result.data as TData) : null;
}

type RecordSourceLike = {
	get: (dataId: string) => Record<string, any> | undefined | null;
};

type ReadFieldsResult = {
	data: Record<string, any>;
	missing: boolean;
};

/* eslint-disable @atlassian/perf-linting/no-quadratic-complexity */
function readFields(
	source: RecordSourceLike,
	recordId: string,
	fields: readonly ReaderSelection[],
	variables: Record<string, any>,
): ReadFieldsResult | null {
	const record = source.get(recordId);
	if (!record) {
		return null;
	}

	const data: Record<string, any> = {};
	let missing = false;

	for (const field of fields) {
		if (field.kind === 'Condition') {
			// Handle @include/@skip directives with variables
			const conditionValue = variables[field.condition];
			const shouldInclude = field.passingValue ? conditionValue : !conditionValue;

			if (shouldInclude) {
				const conditionalData = readFields(source, recordId, field.selections, variables);
				if (conditionalData) {
					Object.assign(data, conditionalData.data);
					missing = conditionalData.missing || missing;
				} else {
					missing = true;
				}
			}
		} else if (field.kind === 'LinkedField') {
			const linkedField = field as ReaderLinkedField;
			const responseKey = linkedField.alias ?? linkedField.name;
			const fieldKey = getRelayFieldStorageKey(linkedField, variables);
			const linkedRef = record[fieldKey];
			const readLinkedRecord = (linkedRecordId: string | null | undefined) => {
				if (linkedRecordId == null) {
					return null;
				}

				const linkedData = readFields(source, linkedRecordId, linkedField.selections, variables);
				if (!linkedData) {
					missing = true;
					return null;
				}

				missing = linkedData.missing || missing;
				return linkedData.data;
			};

			if (linkedField.plural) {
				// Relay stores plural refs in two shapes:
				//  - canonical: { __refs: ['id1', 'id2', ...] }
				//  - tolerated: [{ __ref: 'id1' }, { __ref: 'id2' }]
				if (
					linkedRef &&
					typeof linkedRef === 'object' &&
					!Array.isArray(linkedRef) &&
					Array.isArray((linkedRef as { __refs?: unknown }).__refs)
				) {
					const refs = (linkedRef as { __refs: Array<string | null> }).__refs;
					data[responseKey] = refs.map(readLinkedRecord);
				} else if (Array.isArray(linkedRef)) {
					data[responseKey] = linkedRef.map((ref) => {
						if (ref && typeof ref === 'object' && '__ref' in ref) {
							return readLinkedRecord((ref as { __ref: string }).__ref);
						}
						if (ref != null) {
							missing = true;
						}
						return null;
					});
				} else if (linkedRef === null) {
					data[responseKey] = null;
				} else {
					data[responseKey] = null;
					missing = true;
				}
			} else if (linkedRef && typeof linkedRef === 'object' && '__ref' in linkedRef) {
				data[responseKey] = readLinkedRecord((linkedRef as { __ref: string }).__ref);
			} else if (linkedRef === null) {
				data[responseKey] = null;
			} else {
				data[responseKey] = null;
				missing = true;
			}
		} else if (field.kind === 'ScalarField') {
			const scalarField = field as ReaderScalarField;
			const responseKey = scalarField.alias ?? scalarField.name;
			const fieldKey = getRelayFieldStorageKey(scalarField, variables);
			const value = record[fieldKey];
			if (value === undefined) {
				missing = true;
			} else {
				data[responseKey] = value;
			}
		} else if (field.kind === 'InlineFragment') {
			// Process all inline fragment selections
			const inlineData = readFields(source, recordId, field.selections, variables);
			if (inlineData) {
				Object.assign(data, inlineData.data);
				missing = inlineData.missing || missing;
			} else {
				missing = true;
			}
		}
		// Other selection kinds (e.g., FragmentSpread, ClientExtension) are ignored —
		// readQuery only needs to traverse what the query operation directly selects.
	}

	return { data, missing };
}

/* eslint-enable @atlassian/perf-linting/no-quadratic-complexity */
function readQueryInteropApollo<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: ReadQueryInteropArgs<TVariables>,
): TData | null {
	return window['GLOBAL_APOLLO_CLIENT']!.readQuery<TData, TVariables>({
		...params,
		query: toDocumentNode(params.query),
	});
}
